/**
 * Parses executive summary text into structured sections for display
 */
export function parseExecutiveSummary(summary: string): {
  role: string;
  businessContext: string;
  businessObjectives: string[];
  challenge: string;
} {
  const sections = {
    role: '',
    businessContext: '',
    businessObjectives: [] as string[],
    challenge: ''
  };

  if (!summary) {
    return sections;
  }

  // Check if this is a narrative-style executive summary (no section headers)
  const hasStructuredSections = 
    summary.toLowerCase().includes('your role:') || 
    summary.toLowerCase().includes('role:') ||
    summary.toLowerCase().includes('current business context:') ||
    summary.toLowerCase().includes('business context:') ||
    summary.toLowerCase().includes('your challenge:') ||
    summary.toLowerCase().includes('challenge:');

  // If it's a narrative summary, convert it to structured format
  if (!hasStructuredSections) {
    return parseNarrativeExecutiveSummary(summary);
  }

  // Split the summary into lines
  const lines = summary.split('\n');
  let currentSection = '';
  let tempContent: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Check for section headers (case insensitive)
    if (line.toLowerCase().includes('your role:') || line.toLowerCase().includes('role:')) {
      // Save previous section if any
      if (currentSection && tempContent.length > 0) {
        if (currentSection !== 'businessObjectives') {
          if (currentSection === 'role') {
            sections.role = tempContent.join('\n').trim();
          } else if (currentSection === 'businessContext') {
            sections.businessContext = tempContent.join('\n').trim();
          } else if (currentSection === 'challenge') {
            sections.challenge = tempContent.join('\n').trim();
          }
        }
      }
      currentSection = 'role';
      tempContent = [];
      // Add content from the same line if it exists after the colon
      const colonIndex = line.indexOf(':');
      if (colonIndex !== -1 && line.length > colonIndex + 1) {
        tempContent.push(line.substring(colonIndex + 1).trim());
      }
    } else if (line.toLowerCase().includes('current business context:') || line.toLowerCase().includes('business context:')) {
      if (currentSection && tempContent.length > 0) {
        if (currentSection !== 'businessObjectives') {
          if (currentSection === 'role') {
            sections.role = tempContent.join('\n').trim();
          } else if (currentSection === 'businessContext') {
            sections.businessContext = tempContent.join('\n').trim();
          } else if (currentSection === 'challenge') {
            sections.challenge = tempContent.join('\n').trim();
          }
        }
      }
      currentSection = 'businessContext';
      tempContent = [];
      const colonIndex = line.indexOf(':');
      if (colonIndex !== -1 && line.length > colonIndex + 1) {
        tempContent.push(line.substring(colonIndex + 1).trim());
      }
    } else if (line.toLowerCase().includes('your challenge:') || line.toLowerCase().includes('challenge:')) {
      // Save previous section before switching to challenge
      if (currentSection && tempContent.length > 0) {
        if (currentSection === 'role') {
          sections.role = tempContent.join('\n').trim();
        } else if (currentSection === 'businessContext') {
          const contextText = tempContent.join('\n');
          const bulletPoints = extractBulletPoints(contextText);
          sections.businessObjectives = bulletPoints;
          // Keep the non-bullet text as business context
          sections.businessContext = contextText.split('\n')
            .filter(l => !l.trim().startsWith('•') && !l.trim().startsWith('-') && !l.trim().startsWith('*'))
            .filter(l => !/objectives:\s*$/i.test(l.trim()))
            .join('\n')
            .trim();
        }
      }
      currentSection = 'challenge';
      tempContent = [];
      const colonIndex = line.indexOf(':');
      if (colonIndex !== -1 && line.length > colonIndex + 1) {
        tempContent.push(line.substring(colonIndex + 1).trim());
      }
    } else if (line.length > 0) {
      // Add non-empty lines to current section
      tempContent.push(line);
    }
  }

  // Save the last section
  if (currentSection && tempContent.length > 0) {
    if (currentSection === 'businessContext') {
      const contextText = tempContent.join('\n');
      const bulletPoints = extractBulletPoints(contextText);
      sections.businessObjectives = bulletPoints;
      sections.businessContext = contextText.split('\n')
        .filter(l => !l.trim().startsWith('•') && !l.trim().startsWith('-') && !l.trim().startsWith('*'))
        .filter(l => !/objectives:\s*$/i.test(l.trim()))
        .join('\n')
        .trim();
    } else {
      if (currentSection === 'role') {
        sections.role = tempContent.join('\n').trim();
      } else if (currentSection === 'businessContext') {
        sections.businessContext = tempContent.join('\n').trim();
      } else if (currentSection === 'challenge') {
        sections.challenge = tempContent.join('\n').trim();
      }
    }
  }

  return sections;
}

/**
 * Extracts bullet points from text
 */
function extractBulletPoints(text: string): string[] {
  const lines = text.split('\n');
  const bulletPoints: string[] = [];
  
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('•') || trimmed.startsWith('-') || trimmed.startsWith('*')) {
      // Remove the bullet character and trim
      const content = trimmed.substring(1).trim();
      if (content.length > 0) {
        bulletPoints.push(content);
      }
    }
  }
  
  return bulletPoints;
}

/**
 * Parses narrative-style executive summaries into structured sections
 */
function parseNarrativeExecutiveSummary(summary: string): {
  role: string;
  businessContext: string;
  businessObjectives: string[];
  challenge: string;
} {
  // For narrative summaries, we'll extract the role and create context from the content
  const sentences = summary.split(/[.!?]+/).map(s => s.trim()).filter(s => s.length > 0);
  
  // Extract role information (typically the first part of narrative summaries)
  let role = '';
  let businessContext = '';
  let challenge = '';
  const businessObjectives: string[] = [];

  if (sentences.length > 0) {
    // Find role-related content (look for titles like CEO, CTO, Chief, etc.)
    const roleKeywords = ['CEO', 'Chief Executive', 'Chief Technology', 'Chief Information', 'Chief Risk', 'Chief Communications', 'CTO', 'CISO', 'CCO', 'CRO'];
    const roleSentences = sentences.filter(sentence => 
      roleKeywords.some(keyword => sentence.includes(keyword))
    );
    
    if (roleSentences.length > 0) {
      // Use the first 2-3 sentences that contain role information
      role = roleSentences.slice(0, Math.min(3, roleSentences.length)).join('. ') + '.';
    } else {
      // Fallback: use first sentence if no role keywords found
      role = sentences[0] + '.';
    }

    // Extract business context and challenges from the narrative
    // Look for sentences that mention business situations, objectives, or challenges
    const contextKeywords = ['Board', 'company', 'business', 'revenue', 'growth', 'expansion', 'transformation', 'objectives', 'targets'];
    const challengeKeywords = ['challenge', 'risk', 'threat', 'crisis', 'pressure', 'decision', 'balance', 'navigate', 'protect'];
    
    const contextSentences = sentences.filter(sentence => 
      contextKeywords.some(keyword => sentence.toLowerCase().includes(keyword.toLowerCase()))
    );
    
    const challengeSentences = sentences.filter(sentence => 
      challengeKeywords.some(keyword => sentence.toLowerCase().includes(keyword.toLowerCase()))
    );

    if (contextSentences.length > 0) {
      businessContext = contextSentences.slice(0, 2).join('. ') + '.';
      
      // Extract objectives from context sentences
      contextSentences.forEach(sentence => {
        if (sentence.includes('expansion') || sentence.includes('growth') || sentence.includes('transformation')) {
          businessObjectives.push(sentence.trim());
        }
      });
    }

    if (challengeSentences.length > 0) {
      challenge = challengeSentences.slice(0, 2).join('. ') + '.';
    }

    // If we don't have enough structured content, use the full narrative as the challenge
    if (!challenge && sentences.length > 2) {
      challenge = sentences.slice(-2).join('. ') + '.';
    }
    
    // Ensure we have default business objectives if none were extracted
    if (businessObjectives.length === 0) {
      businessObjectives.push(
        'Navigate complex business challenges',
        'Protect organizational assets and reputation',
        'Maintain stakeholder confidence',
        'Ensure operational continuity'
      );
    }
  }

  return {
    role: role || 'You are leading the organization through a critical period requiring strategic decision-making.',
    businessContext: businessContext || 'The organization faces significant strategic and operational challenges.',
    businessObjectives,
    challenge: challenge || 'Balance competing priorities while protecting the organization from risks and maintaining stakeholder confidence.'
  };
}

/**
 * Gets default executive summary text
 */
export function getDefaultExecutiveSummary(): string {
  return `Your Role:
As Chief Executive Officer of TechCorp International, you lead a Fortune 500 technology company with operations spanning 45 countries and annual revenues exceeding $12 billion. Your leadership has driven consistent growth over the past five years, establishing TechCorp as an industry leader in cloud computing and enterprise software solutions.

Current Business Context:
The Board of Directors has set ambitious targets for the next fiscal year:
• Geographic expansion into emerging markets in Southeast Asia and Latin America
• Integration of advanced AI capabilities across all product lines
• 20% revenue growth while maintaining current profit margins
• Digital transformation of legacy infrastructure systems

These strategic initiatives require significant technology investments and expanded digital infrastructure, substantially increasing your organization's cyber risk exposure.

Your Challenge:
Balance aggressive growth objectives with prudent risk management. Every decision you make will impact your company's security posture, financial performance, and stakeholder confidence. The Board expects you to deliver growth while protecting the organization from potentially catastrophic cyber incidents.

This simulation will test your ability to navigate complex risk scenarios, make strategic decisions under pressure, and allocate resources effectively to protect your organization's assets and reputation.`;
}